import React, { useState } from "react";
import { Input, Textarea, Button } from "@material-tailwind/react";
import { MdClose, MdEdit } from "react-icons/md";
import axios from "axios";
import { UPLOAD_PRODUCT_API } from "../../Api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AddProduct = ({ categoryId, subCategoryId, onClose }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [feature, setFeature] = useState(""); // input value
  const [features, setFeatures] = useState([]); // list of features
  const [images, setImages] = useState([]); // store File objects
  const [previews, setPreviews] = useState([]); // store preview URLs
  const [mainImage, setMainImage] = useState(null);
  const maxImages = 5;

const handleImageChange = (e) => {
  const files = Array.from(e.target.files);

  if (!files.length) return;

  const oversized = files.filter((file) => file.size > 1 * 1024 * 1024);
  if (oversized.length > 0) {
    toast.warning("Some files are larger than 1MB. Please upload smaller images.");
    return;
  }

  setImages((prev) => {
    const allowedFiles = files.slice(0, maxImages - prev.length); // limit new files
    const updated = [...prev, ...allowedFiles];

    // generate previews only for allowed files
    const newPreviews = allowedFiles.map((file) => URL.createObjectURL(file));
    setPreviews((prevP) => [...prevP, ...newPreviews]);

    if (!mainImage && newPreviews.length > 0) {
      setMainImage(newPreviews[0]);
    }

    return updated;
  });
};


  const handleRemoveImage = (img) => {
    const index = previews.indexOf(img);
    if (index !== -1) {
      setPreviews((prev) => prev.filter((_, i) => i !== index));
      setImages((prev) => prev.filter((_, i) => i !== index));
    }

    if (mainImage === img) {
      setMainImage(
        previews.length > 1 ? previews.find((i) => i !== img) : null
      );
    }
  };

  const handleAddProduct = async () => {
    const formData = new FormData();
    formData.append("productName", name);
    formData.append("productDescription", description);
    formData.append("categoryId", categoryId);
    formData.append("subCategoryId", subCategoryId);

    features.forEach((f) => {
      formData.append("productFeatures[]", f);
    });

    // ✅ Append actual file objects
    images.forEach((file) => {
      formData.append("productImages", file);
    });

    try {
      const response = await axios.post(UPLOAD_PRODUCT_API, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Product added successfully:", response.data);
      toast.success("Product added successfully");
      onClose();
    } catch (error) {
      toast.error("Unable to add product");
      console.error("Error adding product:", error);
    }
  };

  const handleAddFeatures = () => {
    if (feature.trim()) {
      setFeatures([...features, feature]); // add new feature to list
      setFeature(""); // clear input
    }
  };

  const handleRemoveFeatures = (index, e) => {
    e.preventDefault();
    setFeatures(features.filter((_, i) => i !== index)); // remove by index
  };

  return (
    <div className="fixed inset-0 z-[1000] flex">
      <div className="hidden sm:block flex-grow bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="relative h-full w-full sm:w-[650px] bg-white shadow-lg flex flex-col">
        <button className="absolute top-4 right-4 text-gray-600 hover:text-red-500" onClick={onClose}>
          <MdClose className="w-6 h-6" />
        </button>
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: "calc(100vh - 60px)" }}>
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {categoryId} &gt; {subCategoryId} &gt; {name || "Add Product"}
          </h2>

          <hr className="mb-4 border-gray-300" />

          <div className="w-full">
            {/* Main Image */}
            <div className="relative group w-full h-48 rounded-lg overflow-hidden shadow-md mb-4 bg-gray-200 flex items-center justify-center">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt="Product"
                  className="w-full h-full object-cover transition-transform duration-300 transform scale-100 group-hover:scale-105"
                />
              ) : null}

              {/* Overlay (only show if no main image) */}
              {!mainImage && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center transition-opacity duration-300 cursor-pointer opacity-100">
                  <label
                    htmlFor="imageUpload"
                    className="text-white flex flex-col items-center cursor-pointer"
                  >
                    <MdEdit className="w-8 h-8" />
                    <span className="text-sm mt-1">you can upload up to 5 images</span>
                  </label>
                  <input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={previews.length >= maxImages}
                  />
                </div>
              )}
            </div>

            {/* Thumbnails + Upload Box */}
            {(previews.length > 0 || mainImage) && (
              <div className="flex gap-3 flex-wrap">
                {previews.map((preview, index) => (
                  <div
                    key={index}
                    className={`relative w-20 h-20 rounded-md overflow-hidden border shadow cursor-pointer ${mainImage === preview ? "ring-2 ring-green-500" : ""
                      }`}
                    onClick={() => setMainImage(preview)}
                  >
                    <img
                      src={preview}
                      alt={`thumbnail-${index}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Remove button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage(preview);
                      }}
                      className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <MdClose size={14} />
                    </button>
                  </div>
                ))}

                {/* Upload box shown near thumbnails if mainImage exists */}
                {previews.length < maxImages && (
                  <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer">
                    <MdEdit className="w-6 h-6 text-gray-500" />
                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                )}

              </div>
            )}
          </div>


          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-1">Product Name</label>
            <Input
              type="text"
              value={name}
              className="border border-gray-300 p-2 rounded-lg"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-1">Description</label>
            <Textarea
              className="w-full border border-gray-300 p-2 rounded-lg"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></Textarea>
          </div>

          <div className="mb-4">

            <div className="flex-1">
              <div className="relative flex flex-col w-full">
                <div className="flex items-center">
                  <Input
                    type="text"
                    label="Features"
                    value={feature}
                    onChange={(e) => setFeature(e.target.value)}
                    className="border p-3 flex-grow rounded-md w-full"
                  />
                  <Button
                    type="button"
                    onClick={handleAddFeatures}
                    className="absolute right-4 bg-green-500 text-white px-4 py-1 rounded-md"
                  >
                    Add
                  </Button>
                </div>

                {/* ✅ Tip below field */}
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Use <span className="font-semibold">":"</span> to separate a heading and its detail.
                  Example → <span className="italic">Heading: Feature1</span>
                </p>
              </div>


              <ul className="list-disc pl-5 mt-3 text-gray-600">
                {features.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <span>{item}</span>
                    <button
                      onClick={(e) => handleRemoveFeatures(index, e)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      ✖
                    </button>
                  </li>
                ))}
              </ul>
            </div>


          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-white shadow-md flex px-4 py-4 gap-3 border-t border-gray-200">
          <button className="flex-1 bg-gray-400 text-white py-3 rounded-lg hover:bg-gray-500" onClick={onClose}>
            Cancel
          </button>
          <button className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600" onClick={handleAddProduct}>
            Add Product
          </button>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AddProduct;
